import React, { useMemo, useState } from 'react';
import { ArrowRight, Calendar, Factory, MapPin, Maximize2, Sun, X, Zap } from '../components/Icons';
import solarParkA from '../images/plant_1.png';
import solarParkB from '../images/plant_2.png';

const parks = [
  {
    id: 'umapur',
    name: 'Umapur Plant',
    location: 'Umapur, Maharashtra',
    capacity: '2 MW',
    status: 'Operational',
    tech: 'Photovoltaic',
    image: solarParkA,
    highlights: [
      'Performance monitoring enabled',
      'O&M ready setup',
      'Grid integration complete',
    ],
    blurb: 'A 2 MW solar power plant in Umapur delivering clean electricity with monitoring and maintenance best practices.',
  },
  {
    id: 'massa',
    name: 'Massa Plant',
    location: 'Massa, Maharashtra',
    capacity: '3 MW',
    status: 'Operational',
    tech: 'Photovoltaic',
    image: solarParkB,
    highlights: [
      'grid-connected',
      'Optimized layout for yield',
      'Monitoring and O&M processes in place',
    ],
    blurb: 'A 3 MW solar power plant in Massa built for high yield and streamlined long-term operations.',
  },
];

function Chip({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-xs transition ' +
        (active
          ? 'border-emerald-300/40 bg-emerald-400/15 text-white'
          : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10')
      }
    >
      {children}
    </button>
  );
}

function Card({ children }) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)] backdrop-blur">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-emerald-400/15 blur-3xl opacity-70 transition group-hover:opacity-100" />
      <div className="relative">{children}</div>
    </div>
  );
}

export default function Parks() {
  const [filter, setFilter] = useState('All');
  const [open, setOpen] = useState(null);

  const filtered = useMemo(() => {
    if (filter === 'All') return parks;
    return parks.filter((p) => p.status === filter);
  }, [filter]);

  const activePark = open ? parks.find((p) => p.id === open) : null;

  return (
    <div className="mx-auto max-w-6xl px-4">
      <section>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs tracking-widest text-white/60">PORTFOLIO</div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Projects</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/70">
              Explore our operating plants. Click a card for detailed highlights.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip active={filter === 'All'} onClick={() => setFilter('All')}>All</Chip>
            <Chip active={filter === 'Operational'} onClick={() => setFilter('Operational')}>Operational</Chip>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {filtered.map((p) => (
            <div key={p.id} className="animate-fadeInUp">
              <Card>
                <div className="relative">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-64 w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1 text-xs text-white/80 backdrop-blur">
                    <Factory className="h-4 w-4" /> {p.status}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xl font-semibold">{p.name}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-white/70">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.location}</span>
                        <span className="inline-flex items-center gap-1"><Zap className="h-4 w-4" /> {p.capacity}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setOpen(p.id)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10"
                    >
                      View <Maximize2 className="h-4 w-4" />
                    </button>
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-white/70">{p.blurb}</p>

                  <div className="mt-5 flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="text-xs text-white/60">Technology</div>
                    <div className="inline-flex items-center gap-2 text-sm">
                      <Sun className="h-4 w-4 text-emerald-200" /> {p.tech}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Modal */}
      {activePark && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fadeIn">
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => setOpen(null)}
            />

            <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-neutral-950/70 backdrop-blur-xl animate-popIn">
              <div className="relative">
                <img src={activePark.image} alt={activePark.name} className="h-72 w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <button
                  onClick={() => setOpen(null)}
                  className="absolute right-4 top-4 inline-flex items-center justify-center rounded-xl border border-white/10 bg-black/40 p-2 text-white hover:bg-black/60"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 left-4">
                  <div className="text-2xl font-semibold">{activePark.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-white/70">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {activePark.location}</span>
                    <span className="inline-flex items-center gap-1"><Zap className="h-4 w-4" /> {activePark.capacity}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {activePark.status}</span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-white/60">Technology</div>
                    <div className="mt-1 text-sm font-semibold">{activePark.tech}</div>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{activePark.blurb}</p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-xs text-white/60">Highlights</div>
                    <ul className="mt-2 space-y-2 text-sm text-white/70">
                      {activePark.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-300/80" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-end">
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-400/90 px-5 py-3 text-sm font-semibold text-black hover:bg-emerald-300"
                  >
                    Partner with us <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

            </div>
        </div>
      )}
    </div>
  );
}
